import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { Check, X, Mail, ArrowLeft } from 'lucide-react';
import Header from '../components/blog/Header';
import Footer from '../components/blog/Footer';
import { Container } from '../components/ui/Container';
import { Button } from '../components/ui/Button';
import SEO from '../components/seo/SEO';
import Spinner from '../components/ui/Spinner';
import { confirmSubscription } from '../services/newsletterService';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
  display: flex;
  flex-direction: column;
`;

const Main = styled.main`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;
  
  ${Container} {
    max-width: 32rem;
  }
`;

const Content = styled.div`
  text-align: center;
  background-color: ${props => props.theme.colors.card};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  padding: 3rem 2rem;
  
  @media (min-width: 768px) {
    padding: 4rem 3rem;
  }
`;

const IconContainer = styled.div`
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1.5rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => {
    if (props.$type === 'success') return props.theme.colors.success || '#10b98122';
    if (props.$type === 'error') return props.theme.colors.error || '#ef444422';
    return props.theme.colors.muted;
  }};
  color: ${props => {
    if (props.$type === 'success') return props.theme.colors.success || '#10b981';
    if (props.$type === 'error') return props.theme.colors.error || '#ef4444';
    return props.theme.colors.mutedForeground;
  }};
  
  @media (min-width: 1024px) {
    width: 5rem;
    height: 5rem;
    margin-bottom: 2rem;
  }
  
  svg {
    width: 2rem;
    height: 2rem;
    
    @media (min-width: 1024px) {
      width: 2.5rem;
      height: 2.5rem;
    }
  }
`;

const Title = styled.h1`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1.75rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    font-size: 2rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
  }
`;

const Message = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.colors.mutedForeground};
  line-height: 1.7;
  margin-bottom: 2rem;
  
  @media (min-width: 1024px) {
    font-size: 1.125rem;
    margin-bottom: 2.5rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const EmailIcon = styled.div`
  width: 3rem;
  height: 3rem;
  margin: 0 auto 1.5rem;
  color: ${props => props.theme.colors.mutedForeground};
  opacity: 0.6;
  
  @media (min-width: 1024px) {
    width: 4rem;
    height: 4rem;
    margin-bottom: 2rem;
  }
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const ConfirmSubscription = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const confirm = async () => {
      if (!token) {
        setStatus('error');
        setMessage('No confirmation token provided. Please check your email and use the link provided.');
        return;
      }

      try {
        const result = await confirmSubscription(token);
        
        if (result.success) {
          setStatus('success');
          setMessage(result.message);
        } else {
          setStatus('error');
          setMessage(result.message);
        }
      } catch (error) {
        console.error('Confirmation error:', error);
        setStatus('error');
        setMessage('Unable to confirm your subscription. Please try again or contact support.');
      }
    };

    confirm();
  }, [token]);

  return (
    <PageWrapper>
      <SEO
        title="Confirm Subscription"
        description="Confirm your newsletter subscription"
        noindex
      />
      <Header />
      <Main>
        <Container>
          <Content>
            {status === 'loading' && (
              <>
                <IconContainer>
                  <Spinner size="lg" />
                </IconContainer>
                <Title>Confirming Subscription...</Title>
                <Message>
                  Please wait while we confirm your newsletter subscription.
                </Message>
              </>
            )}

            {status === 'success' && (
              <>
                <IconContainer $type="success">
                  <Check />
                </IconContainer>
                <Title>Subscription Confirmed!</Title>
                <Message>
                  {message || 'Your subscription has been confirmed. You will now receive our latest articles and updates.'}
                </Message>
                <ButtonGroup>
                  <Button as={Link} to="/" variant="primary">
                    <ArrowLeft />
                    Back to Home
                  </Button>
                </ButtonGroup>
              </>
            )}

            {status === 'error' && (
              <>
                <IconContainer $type="error">
                  <X />
                </IconContainer>
                <Title>Confirmation Failed</Title>
                <Message>
                  {message || 'The confirmation link is invalid or has expired. Please subscribe again or contact support if you continue to have issues.'}
                </Message>
                <ButtonGroup>
                  <Button as={Link} to="/" variant="primary">
                    <ArrowLeft />
                    Back to Home
                  </Button>
                  <Button as={Link} to="/contact" variant="secondary">
                    Contact Support
                  </Button>
                </ButtonGroup>
              </>
            )}

            {!token && status !== 'loading' && (
              <>
                <EmailIcon>
                  <Mail />
                </EmailIcon>
                <Title>Check Your Email</Title>
                <Message>
                  We've sent you a confirmation email. Please click the link in the email to confirm your subscription.
                </Message>
                <ButtonGroup>
                  <Button as={Link} to="/" variant="primary">
                    <ArrowLeft />
                    Back to Home
                  </Button>
                </ButtonGroup>
              </>
            )}
          </Content>
        </Container>
      </Main>
      <Footer />
    </PageWrapper>
  );
};

export default ConfirmSubscription;



